import type { ReactElement } from 'react';
import {
  dataAsOf,
  formatDate,
  formatNumber,
  type DatasetFreshness,
  type DatasetLinkSummary,
  type Report
} from '@tallyframe/core';
import { ApiError } from '../api/errors';
import { Banner } from '../components/ErrorBanner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Stack } from '../ui/Stack';
import { LinkSection } from '../sources/LinkSection';
import { fetchedText } from './known-hosts';

/**
 * Last-resort safety net for a failure kind this panel has no explicit
 * wording rule for (spec §10.1 bans this vocabulary from the screen). It is
 * not smart: it matches on words, so it discards a legitimate, actionable
 * message exactly as readily as it discards genuine internal vocabulary —
 * that is what happened to `NOT_FOUND`'s "Dataset has no snapshots yet, so
 * there is nothing to refresh onto." before `hasNeverReceivedData` below
 * gave that reachable state its own sentence. Any state a person can
 * actually reach should get an explicit sentence derived from props, not
 * from matching the server's message, before it ever falls through to this
 * fallback.
 */
const INTERNAL_WORDING = /snapshot|snap_|unverified/i;

function safeMessage(message: string, fallback: string): string {
  return INTERNAL_WORDING.test(message) ? fallback : message;
}

/**
 * `FreshnessPanel`'s props (declared inline there, at
 * `apps/web/src/reports/FreshnessPanel.tsx:24`), so the editor can swap this
 * panel in without touching the caller. Every addition below is optional, so
 * a plain `FreshnessPanel`-shaped value still satisfies this type and a value
 * of this type still satisfies `FreshnessPanel`'s — the two stay assignable
 * both ways:
 *
 *  - `freshness.latestReceivedAt` / `latestSourceObservedAt` mirror the
 *    active snapshot's own `receivedAt` / `sourceObservedAt`, but for the
 *    newer data behind `refreshAvailable`. `DatasetFreshness`
 *    (`packages/core/src/service.ts`) does not carry this yet, even though
 *    the server already has it in hand (`reporting-service.ts`'s
 *    `freshness()` looks up `latest` but only forwards its id). Until a task
 *    puts it on the wire, the "Newer data is available" banner degrades to
 *    omitting the date rather than guessing one.
 *  - `columnLabels` resolves a `SNAPSHOT_INCOMPATIBLE` issue's raw
 *    `location.columnId` to the label a person recognizes — the same map
 *    shape `IssueList` / `describeIssueLocation` already use elsewhere
 *    (`apps/web/src/api/errors.ts`). Without an entry for the missing
 *    column, the panel names that it's missing a column without naming
 *    which one; it never falls back to the server's raw message, which
 *    always talks about snapshots and ids.
 */
export type FreshnessPanelProps = {
  report: Report;
  freshness: DatasetFreshness & {
    latestReceivedAt?: string | null;
    latestSourceObservedAt?: string | null;
  };
  refreshing: boolean;
  refreshError: ApiError | null;
  onRefresh(): void;
  onDismissRefreshError(): void;
  columnLabels?: Record<string, string>;
  /** The dataset's linked Google Sheet, if any. */
  link?: DatasetLinkSummary | null;
  /** The Picker's public identifiers; present only when Google Sheets is enabled. */
  googleSheets?: { apiKey: string; appId: string } | null;
  /** The OAuth callback's `?google_error=` code. */
  googleError?: string;
};

/**
 * Source, data-as-of and row count for the dataset behind a report, plus the
 * controls to pull in newer data. Replaces `FreshnessPanel` (spec §9.2,
 * §10.2): no snapshot ids, no "unverified claim" framing — a source is
 * stated plainly, and a missing column is named by its label, not its id.
 */
export function DataPanel(props: FreshnessPanelProps): ReactElement {
  const { freshness, refreshError } = props;
  const incompatible = refreshError?.code === 'SNAPSHOT_INCOMPATIBLE';
  const conflicted = refreshError?.code === 'SNAPSHOT_CONFLICT';

  const dataAsOfText = freshness.receivedAt
    ? formatDate(dataAsOf({ receivedAt: freshness.receivedAt, sourceObservedAt: freshness.sourceObservedAt ?? undefined }))
    : 'No data yet';

  const rowsText = freshness.rowCount === null ? '—' : formatNumber(freshness.rowCount);

  const newerDateText = freshness.latestReceivedAt
    ? formatDate(
        dataAsOf({
          receivedAt: freshness.latestReceivedAt,
          sourceObservedAt: freshness.latestSourceObservedAt ?? undefined
        })
      )
    : null;

  const missingColumnIssue = refreshError?.issues.find((issue) => issue.location?.columnId);
  const missingColumnId = missingColumnIssue?.location?.columnId ?? null;
  const missingColumnLabel = missingColumnId ? (props.columnLabels?.[missingColumnId] ?? null) : null;

  // The server's own error text always describes the *snapshot* mechanics
  // (ids, schema fingerprints, "Re-read the report before refreshing") — never
  // safe to show verbatim for a refusal this panel already has a wording rule
  // for. `refreshError.message` is only ever shown for a failure kind neither
  // of those rules covers, and even then only once it passes `safeMessage`.
  const incompatibleText = missingColumnId
    ? missingColumnLabel
      ? `The new data is missing a column this report uses: ${missingColumnLabel}.`
      : 'The new data is missing a column this report uses.'
    : "The new data doesn't match this report's columns.";

  const conflictedText =
    'Somebody updated this report while your refresh was in progress. Reload to see the current version.';

  // `freshness.latestSnapshotId === null` means the dataset behind this
  // report has never received any data at all — refreshing can only ever
  // fail the same way, forever, until data is sent through a different flow
  // (CSV upload or an agent's submit_data call). That is a reachable, known
  // state, so it gets its own sentence read from props rather than falling
  // through to `safeMessage`, which would otherwise flatten the server's
  // specific "nothing to refresh onto" into a generic "try again" that hides
  // the one fact that actually matters here.
  const hasNeverReceivedData = freshness.latestSnapshotId === null;
  const noDataText = 'No data has been sent for this report yet.';

  const refreshErrorText = refreshError
    ? incompatible
      ? incompatibleText
      : conflicted
        ? conflictedText
        : hasNeverReceivedData
          ? noDataText
          : safeMessage(refreshError.message, 'The refresh failed. Try again.')
    : null;

  const refreshDisabled = hasNeverReceivedData;

  return (
    <section data-testid="data-panel" aria-busy={props.refreshing}>
      <Card>
        <Stack gap={3}>
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 p-0 text-lg font-semibold text-ink font-ui">Data</h2>
          <Button
            size="sm"
            data-testid="data-refresh-button"
            onPress={props.onRefresh}
            busy={props.refreshing}
            busyLabel="Refreshing…"
            isDisabled={refreshDisabled}
          >
            Refresh data
          </Button>
        </div>

        <dl className="m-0 p-0 flex flex-col gap-1 font-ui text-sm text-ink">
          <div className="m-0 p-0 flex gap-4">
            <dt className="m-0 p-0 w-data-label shrink-0 text-muted">Source</dt>
            <dd data-testid="data-source" className="m-0 p-0">
              {freshness.sourceDescription ? (
                freshness.sourceDescription
              ) : (
                <span className="text-muted">No source given</span>
              )}
            </dd>
          </div>
          {freshness.fetchedHost && freshness.fetchedAt ? (
            <div className="m-0 p-0 flex gap-4">
              <dt className="m-0 p-0 w-data-label shrink-0 text-muted">Fetched</dt>
              <dd data-testid="data-fetched" className="m-0 p-0">
                {fetchedText(freshness.fetchedHost, freshness.fetchedAt, formatDate)}
              </dd>
            </div>
          ) : null}
          <div className="m-0 p-0 flex gap-4">
            <dt className="m-0 p-0 w-data-label shrink-0 text-muted">Data as of</dt>
            <dd data-testid="data-as-of" className="m-0 p-0">{dataAsOfText}</dd>
          </div>
          <div className="m-0 p-0 flex gap-4">
            <dt className="m-0 p-0 w-data-label shrink-0 text-muted">Rows</dt>
            <dd data-testid="data-row-count" className="m-0 p-0 tabular-nums">
              {rowsText}
            </dd>
          </div>
        </dl>

        <LinkSection
          datasetId={props.report.datasetId}
          reportId={props.report.id}
          link={props.link ?? null}
          googleSheets={Boolean(props.googleSheets)}
          picker={props.googleSheets ?? undefined}
          googleError={props.googleError}
        />

        {freshness.refreshAvailable ? (
          <div data-testid="refresh-available">
            <Banner
              tone="info"
              title={newerDateText ? `Newer data is available (${newerDateText})` : 'Newer data is available'}
              actions={
                <Button
                  variant="primary"
                  size="sm"
                  data-testid="refresh-button"
                  onPress={props.onRefresh}
                  busy={props.refreshing}
                  busyLabel="Refreshing…"
                >
                  Use the latest data
                </Button>
              }
            />
          </div>
        ) : null}

        {refreshError ? (
          <Banner
            tone="danger"
            title={
              incompatible
                ? "The new data doesn't fit this report"
                : conflicted
                  ? 'Somebody updated this report first'
                  : 'The refresh failed'
            }
            testId="refresh-error"
            errorCode={refreshError.code}
            actions={
              <Button size="sm" onPress={props.onDismissRefreshError}>
                Dismiss
              </Button>
            }
          >
            {refreshErrorText}
          </Banner>
        ) : null}
        </Stack>
      </Card>
    </section>
  );
}
